import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

/** Quiet "← Back to list" link used at the top of detail pages. */
export const BackLink = ({ to, children, testId }) => (
  <Link to={to} className="inline-flex h-6 items-center gap-1 text-[12.5px] text-muted-foreground transition-colors duration-150 hover:text-foreground" data-testid={testId}>
    <ArrowLeft className="h-3.5 w-3.5" /> {children}
  </Link>
);

/** Linear progress through an ordered status list. Thin segments, no pills. */
export const StatusSteps = ({ steps, current, labelFn = (s) => s, className, testId = "status-steps" }) => {
  const idx = steps.indexOf(current);
  return (
    <div className={className} data-testid={testId}>
      <ol className="flex items-center gap-1" aria-label="Fremdrift">
        {steps.map((s, i) => (
          <li key={s} className="flex-1" title={labelFn(s)}>
            <div className={cn("h-1 rounded-full", i < idx ? "bg-foreground/50" : i === idx ? "bg-primary" : "bg-muted")} />
          </li>
        ))}
      </ol>
      <div className="mt-1.5 flex items-baseline justify-between text-[12px]">
        <span className="font-medium">{labelFn(current)}</span>
        <span className="text-muted-foreground tabular">
          Steg {Math.max(1, idx + 1)} av {steps.length}
        </span>
      </div>
    </div>
  );
};

/** Page title row. Quiet: 20px semibold title, optional count, small actions to the right. */
export const PageHeader = ({ title, subtitle, actions, children, className, testId, eyebrow, count, leading }) => (
  <div className={cn("flex flex-col gap-3 md:flex-row md:items-start md:justify-between", className)} data-testid={testId}>
    <div className={cn("min-w-0", leading && "flex items-start gap-3")}>
      {leading && <div className="mt-0.5 shrink-0">{leading}</div>}
      <div className="min-w-0">
        {eyebrow && <div className="mb-0.5 text-[12px] text-muted-foreground">{eyebrow}</div>}
        <div className="flex min-w-0 items-baseline gap-2">
          <h1 className="truncate text-[20px] font-semibold leading-7 tracking-tight">{title}</h1>
          {count != null && <span className="shrink-0 text-[13px] text-muted-foreground tabular">{count}</span>}
        </div>
        {subtitle && <div className="mt-0.5 text-[13px] text-muted-foreground">{subtitle}</div>}
        {children}
      </div>
    </div>
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/** Bordered content block with a compact header. No shadows. */
export const Section = ({ title, actions, children, className, description, bodyClassName, testId }) => (
  <section className={cn("rounded-md border bg-card", className)} data-testid={testId}>
    {(title || actions) && (
      <header className="flex min-h-10 items-center justify-between gap-3 border-b px-4 py-2">
        <div className="min-w-0">
          <h2 className="truncate text-[13px] font-medium leading-5">{title}</h2>
          {description && <p className="text-[12px] leading-4 text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
    )}
    <div className={cn("p-4", bodyClassName)}>{children}</div>
  </section>
);

/** Label/value pair for detail views. */
export const Field = ({ label, value, mono, className, children }) => (
  <div className={cn("min-w-0", className)}>
    <div className="text-[12px] leading-4 text-muted-foreground">{label}</div>
    <div className={cn("mt-0.5 break-words text-[13px]", mono && "font-mono text-[12.5px]")}>
      {children ?? (value === null || value === undefined || value === "" ? <span className="text-muted-foreground">–</span> : value)}
    </div>
  </div>
);

/** Filter row above tables. */
export const Toolbar = ({ children, className, right, testId }) => (
  <div className={cn("flex flex-wrap items-center gap-2", className)} data-testid={testId}>
    {children}
    {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
  </div>
);

/** Pressable filter chip (outline → filled when active). */
export const FilterToggle = ({ pressed, onClick, children, testId, className }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={!!pressed}
    data-testid={testId}
    className={cn(
      "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-[13px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
      pressed ? "border-foreground/30 bg-muted font-medium text-foreground" : "border-input bg-background text-foreground/80 hover:bg-muted",
      className
    )}
  >
    {children}
  </button>
);

/** Search field with icon and clear button. */
export const SearchInput = ({ value, onChange, placeholder = "Søk…", className, testId, autoFocus, inputClassName }) => (
  <div className={cn("relative w-56", className)}>
    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cn("h-8 pl-8 pr-7", inputClassName)} data-testid={testId} autoFocus={autoFocus} />
    {value ? (
      <button type="button" onClick={() => onChange("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label="Tøm søk">
        <X className="h-3.5 w-3.5" />
      </button>
    ) : null}
  </div>
);

const VALUE_TONE = { default: "", warning: "text-amber-700 dark:text-amber-300", danger: "text-rose-700 dark:text-rose-300", success: "" };

/**
 * One metric. Meant to live inside <KpiGrid> (cells separated by hairlines – no cards, no icons).
 * `icon` is accepted for backwards compatibility and ignored.
 */
export const KpiTile = ({ label, value, hint, tone = "default", onClick, testId, className }) => {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testId || `kpi-tile-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={cn(
        "flex min-w-0 flex-col justify-between gap-2 bg-card px-4 py-3 text-left",
        onClick && "transition-colors duration-150 hover:bg-muted/50 focus-visible:bg-muted/60 focus-visible:outline-none",
        className
      )}
    >
      <div className="truncate text-[12px] leading-4 text-muted-foreground">{label}</div>
      <div className="min-w-0">
        <div className={cn("text-[22px] font-semibold leading-7 tracking-tight tabular", VALUE_TONE[tone] || "")}>{value}</div>
        {hint && <div className="mt-0.5 truncate text-[11.5px] leading-4 text-muted-foreground">{hint}</div>}
      </div>
    </Comp>
  );
};

/** Hairline-separated metric strip. */
export const KpiGrid = ({ children, className, cols = "grid-cols-2 md:grid-cols-4", testId }) => (
  <div className={cn("grid gap-px overflow-hidden rounded-md border bg-border", cols, className)} data-testid={testId}>
    {children}
  </div>
);

/** Quiet section for the operational "lens" pages: heading + optional description, hairlines instead of card chrome. */
export const Lens = ({ title, description, actions, children, testId, className }) => (
  <section className={cn("space-y-3", className)} data-testid={testId}>
    <div className="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 className="text-[13.5px] font-semibold tracking-tight">{title}</h2>
        {description && <p className="text-[12px] leading-4 text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
    {children}
  </section>
);
